// The standup Tickets tab — "the shape of the desk" (D1-ui.md Stage 2).
//
// Isaac opens this first every morning, reads the heatmap, and decides what
// kind of day it is. Composition only: the pulse line + heatmap + selection
// strip come from `DeskHeatmap`, the board and side rail from `DeskBoard`, the
// drawer from `TicketDrawer`, and every number is computed in `deskModel`.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../../design/brainTheme";
import { getStandupUrgent, type UrgentResult } from "../../lib/api";
import { DeskHeatmap } from "../../mui/DeskHeatmap";
import { DeskWorkBoard, DeskSideRail } from "../../mui/DeskBoard";
import { TicketDrawer } from "../../mui/TicketDrawer";
import { buildAxes, type DeskAxis, type DeskSelection } from "../../mui/deskModel";

const c = brainTokens.color;

const POLL_MS = 60_000;

// Session cache: switching away and back to this tab re-renders the last desk
// instantly and refreshes in the background, instead of a spinner on every
// tab switch. Keyed by date; the 60s poll keeps it honest.
const deskCache = new Map<string, UrgentResult>();

// `handled` is a personal "I've seen/actioned this" marker for the desk walk —
// it does not write to Jira (see DeskHeatmap's confirmation-badge tooltip).
// It's session state, not server state, so it needs its own cache alongside
// deskCache: without this, switching to the SLA view and back (or to another
// standup tab) silently wiped every checkmark, even though the tickets
// themselves reloaded instantly from deskCache (D2 critique finding).
const handledCache = new Map<string, Record<string, string>>();

export function TicketsDeskTab({
  date,
  attendees,
  footer,
}: {
  date: string;
  /** Who is in the room — stamped onto the standup note. */
  attendees?: string[];
  /** Rendered under the desk (the SLA view toggle lives here). */
  footer?: React.ReactNode;
}) {
  const [result, setResult] = useState<UrgentResult | null>(() => deskCache.get(date) ?? null);
  const [error, setError] = useState<string | null>(null);
  const [axis, setAxis] = useState<DeskAxis>("age");
  const [selection, setSelection] = useState<DeskSelection | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [handled, setHandled] = useState<Record<string, string>>(() => handledCache.get(date) ?? {});
  const [pivotCustomer, setPivotCustomer] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) =>
      getStandupUrgent(date || undefined, signal)
        .then((r) => {
          deskCache.set(date, r);
          setResult(r);
          setError(r.error ?? null);
        })
        .catch((e) => {
          if (e?.name !== "AbortError") setError(e instanceof Error ? e.message : String(e));
        }),
    [date]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    const id = window.setInterval(() => void load(), POLL_MS);
    return () => {
      ctrl.abort();
      window.clearInterval(id);
    };
  }, [load]);

  // Resync `handled` if `date` changes without a remount (e.g. history nav) —
  // the lazy useState initializer above only runs once.
  useEffect(() => {
    setHandled(handledCache.get(date) ?? {});
  }, [date]);

  const tickets = useMemo(() => result?.tickets ?? [], [result]);

  /**
   * The side rail's "who is having a bad week" pivots the heatmap onto the
   * Customer axis, selected on that customer's worst ball-with-us row.
   */
  const pivotToCustomer = useCallback(
    (customer: string) => {
      const axes = buildAxes(tickets, { pinCustomer: customer });
      const column = axes.customer.columns.indexOf(customer);
      setAxis("customer");
      setPivotCustomer(customer);
      const theirs = tickets.filter((t) => (t.customer ?? t.account) === customer);
      const withUs = theirs.filter((t) => t.deskState === "needs_reply" || t.deskState === "needs_decision");
      const state = (withUs[0] ?? theirs[0])?.deskState ?? "needs_reply";
      setSelection({ state, column: column >= 0 ? column : axes.customer.columns.length - 1 });
    },
    [tickets]
  );

  const onAxisChange = useCallback((next: DeskAxis) => {
    setAxis(next);
    setPivotCustomer(null);
  }, []);

  const onAct = useCallback(
    (key: string, confirmation: string) => {
      setHandled((prev) => {
        const next = { ...prev, [key]: confirmation };
        handledCache.set(date, next);
        return next;
      });
    },
    [date]
  );

  if (error && !result) {
    return (
      <Box sx={{ padding: "22px", fontSize: 14, color: c.dangerText }}>
        Could not load the support desk: {error}
      </Box>
    );
  }

  if (!result) {
    return <Box sx={{ padding: "22px", fontSize: 14, color: c.muted }}>Loading the support desk…</Box>;
  }

  return (
    <Box>
      {error && (
        <Box sx={{ fontSize: 13, color: c.warnText, mb: "10px" }}>
          Showing the last good read — the desk refresh failed: {error}
        </Box>
      )}

      <DeskHeatmap
        tickets={tickets}
        desk={result.desk}
        fetchedAt={result.fetchedAt}
        truncated={result.truncated}
        pinCustomer={pivotCustomer}
        axis={axis}
        onAxisChange={onAxisChange}
        selection={selection}
        onSelectionChange={(s) => {
          setSelection(s);
          if (!s) setPivotCustomer(null);
        }}
        onOpenTicket={setOpenKey}
        handled={handled}
        onAct={onAct}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) 352px",
          gap: "18px",
          mt: "16px",
          alignItems: "start",
          "@media (max-width: 1280px)": { gridTemplateColumns: "1fr" },
        }}
      >
        <DeskWorkBoard tickets={tickets} onOpenTicket={setOpenKey} />
        <DeskSideRail
          tickets={tickets}
          desk={result.desk}
          fetchedAt={result.fetchedAt}
          activeCustomer={pivotCustomer}
          onPivotToCustomer={pivotToCustomer}
        />
      </Box>

      {footer}

      <TicketDrawer
        ticketKey={openKey}
        date={result.date || date}
        attendees={attendees}
        onClose={() => {
          setOpenKey(null);
          // The drawer may have posted a note or stamped the ticket; pick that
          // up on the next poll rather than leaving a stale desk on screen.
          void load();
        }}
      />
    </Box>
  );
}
